import { FormTemplate, Assessment, FormAssignment } from "../types";
import { UploadResponse } from "../api/endpoints/upload";

export const mockSampleTemplate: FormTemplate = {
  id: "1",
  type: "newTemplate",
  name: "Community First Choice Assessment",
  description: "Quarterly self-assessment of goals and achievements",
  createdBy: "11111111-1111-1111-1111-111111111111",
  published: true,
  version: "1.0",
  formSchema: {
    sections: [
      {
        id: "section-1",
        title: "Goal Achievement",
        description: "Review your quarterly goals",
        subsections: [
          {
            id: "subsection-1-1",
            title: "Q1 Goals",
            estimatedMinutes: 5,
            fields: [
              {
                id: "goalsAchieved",
                type: "textarea",
                label: "What goals did you achieve?",
                required: true,
              },
              {
                id: "challenges",
                type: "textarea",
                label: "What challenges did you face?",
                required: true,
              },
            ],
          },
        ],
      },
      {
        id: "section-2",
        title: "Skills Development",
        description: "Your professional growth",
        subsections: [
          {
            id: "subsection-2-1",
            title: "New Skills",
            estimatedMinutes: 4,
            fields: [
              {
                id: "skillsLearned",
                type: "textarea",
                label: "What new skills did you learn?",
                required: true,
              },
            ],
          },
        ],
      },
    ],
  },
  settings: {
    offlineMode: true,
    retakeAllowed: true,
    signatureRequired: true,
  },
  createdAt: "2024-02-01T00:00:00Z",
  updatedAt: "2024-02-01T00:00:00Z",
};

export const mockTemplates: FormTemplate[] = [
  {
    id: "aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa",
    type: "oldTemplate",
    name: "Q1 Performance Self-Review",
    description:
      "Annual feedback survey to help us improve workplace culture and employee experience",
    createdBy: "11111111-1111-1111-1111-111111111111",
    published: true,
    version: "2.1",
    formSchema: {
      sections: [
        {
          id: "section-1",
          title: "Personal Information",
          description: "Basic information about you",
          subsections: [
            {
              id: "subsection-1-1",
              title: "Basic Info",
              estimatedMinutes: 2,
              fields: [
                {
                  id: "firstName",
                  type: "text",
                  label: "First Name",
                  required: true,
                },
                {
                  id: "lastName",
                  type: "text",
                  label: "Last Name",
                  required: true,
                },
                {
                  id: "employeeId",
                  type: "text",
                  label: "Employee ID",
                  required: true,
                },
              ],
            },
            {
              id: "subsection-1-2",
              title: "Demographics",
              estimatedMinutes: 2,
              fields: [
                {
                  id: "department",
                  type: "select",
                  label: "Department",
                  required: true,
                  options: ["Engineering", "Sales", "Marketing", "HR"],
                },
                {
                  id: "role",
                  type: "text",
                  label: "Job Title",
                  required: true,
                },
              ],
            },
          ],
        },
        {
          id: "section-2",
          title: "Contact Information",
          description: "How can we reach you?",
          subsections: [
            {
              id: "subsection-2-1",
              title: "Email & Phone",
              estimatedMinutes: 2,
              fields: [
                {
                  id: "email",
                  type: "email",
                  label: "Email Address",
                  required: true,
                },
                {
                  id: "phone",
                  type: "tel",
                  label: "Phone Number",
                  required: true,
                },
              ],
            },
            {
              id: "subsection-2-2",
              title: "Address & Preferences",
              estimatedMinutes: 3,
              fields: [
                {
                  id: "address",
                  type: "text",
                  label: "Street Address",
                  required: true,
                },
                {
                  id: "contactMethod",
                  type: "select",
                  label: "Preferred Contact Method",
                  required: true,
                  options: ["Email", "Phone", "Text"],
                },
              ],
            },
          ],
        },
        {
          id: "section-3",
          title: "Job Satisfaction",
          description: "Tell us about your experience",
          subsections: [
            {
              id: "subsection-3-1",
              title: "Overall Satisfaction",
              estimatedMinutes: 3,
              fields: [
                {
                  id: "overallSatisfaction",
                  type: "rating",
                  label: "Overall job satisfaction",
                  required: true,
                  scale: 5,
                },
                {
                  id: "workLifeBalance",
                  type: "rating",
                  label: "Work-life balance",
                  required: true,
                  scale: 5,
                },
              ],
            },
          ],
        },
      ],
    },
    settings: {
      offlineMode: true,
      retakeAllowed: true,
      signatureRequired: true,
    },
    createdAt: "2024-01-15T00:00:00Z",
    updatedAt: "2024-01-15T00:00:00Z",
  },
];

export const mockAssessments: Assessment[] = [
  {
    id: "assess-1",
    templateId: "aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa",
    userId: "22222222-2222-2222-2222-222222222222",
    formData: {},
    status: "draft",
    currentSection: 0,
    currentSubsection: 0,
    progressPercent: 0,
    startedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1,
  },
  {
    id: "assess-2",
    templateId: "bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb",
    userId: "22222222-2222-2222-2222-222222222222",
    formData: {
      goalsAchieved: "Completed new feature implementation",
      challenges: "Time management and resource allocation",
    },
    status: "in_progress",
    currentSection: 0,
    currentSubsection: 1,
    progressPercent: 35,
    startedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    version: 1,
  },
];

export const mockListItem = {
  "id": "previewTemplate",
  "userEmail": "admin@company.com",
  "fileTitle": "Preview: Employee Satisfaction Survey 2024",
  "fileName": "test-file.pdf",
  "createdAt": "2025-11-11T17:42:53.25Z",
  "fileId": "mock-template-001",
  "htmlFileId": {
    "timestamp": 1762882973,
    "creationTime": "2025-11-11T17:42:53Z"
  },
  "published": "Published",
  "pdfType": "XFA_PDF"
}

export const mockAcroListItem = {
  "id": "mockAcroTemplate",
  "userEmail": "admin@company.com",
  "fileTitle": "Sample Acro template",
  "fileName": "test-file.pdf",
  "createdAt": "2025-11-11T17:42:53.25Z",
  "fileId": "mock-acro-template-001",
  "htmlFileId": {
    "timestamp": 1762882973,
    "creationTime": "2025-11-11T17:42:53Z"
  },
  pdfType: "ACROFORM_PDF",
  isAcroFile: true
}

export const mockAcroTemplateDetails = {
  "fileId": "mock-acro-template-001",
  "userEmail": "admin@company.com",
  "htmlContent": '',
  "fileName": "2067.pdf",
  "title": "Case Information",
  "pdfType": "ACROFORM_PDF"
}

export const acroUploadResponse = {
  "fileId": "mock-acro-template-001",
  "fileName": "2067.pdf",
  "message": "PDF uploaded successfully.",
  "title": "Case Information",
  pdfType: "ACROFORM_PDF",
  content: "",
} as const satisfies UploadResponse

export const mockAssignments: FormAssignment[] = [
  {
    id: "assign-1",
    templateId: "aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa",
    userId: "22222222-2222-2222-2222-222222222222",
    dueDate: "2024-03-31T23:59:59Z",
    assignedAt: "2024-01-01T00:00:00Z",
    required: true,
  },
  {
    id: "assign-2",
    templateId: "bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb",
    userId: "22222222-2222-2222-2222-222222222222",
    dueDate: "2024-04-15T23:59:59Z",
    assignedAt: "2024-02-01T00:00:00Z",
    required: true,
  },
];

export const getTemplateStats = () => ({
  totalForms: 24,
  completed: 156,
  completionRate: 89,
  pending: 43,
  overdue: 23,
});

export const getAssessmentsByTemplate = (templateId: string) => {
  return mockAssessments.filter((a) => a.templateId === templateId);
};
